<script setup lang="ts">
import { Terminal } from 'lucide-vue-next'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import RainbowButtonBig from '@/components/utils/RainbowButtonBig.vue'

const props = withDefaults(defineProps<Props>(), {
	position: 'left',
	reverse: false,
})

const router = useRouter()

const { t } = useI18n()

interface Props {
	position?: string
	reverse?: boolean
	titleTag?: string
}

const position = computed(() => {
	return {
		'justify-center md:justify-start': props.position === 'left',
		'justify-center md:justify-end': props.position === 'right',
	}
})

const textAlignment = computed(() => {
	return {
		'md:!text-left': props.position === 'left',
		'md:!text-right': props.position === 'right',
	}
})
const imageAlignment = computed(() => {
	return {
		'md:!justify-start': !props.reverse,
		'md:!justify-end': props.reverse,
	}
})
</script>

<template>
	<div
		class="w-full relative flex gap-2 flex-col md:flex-row flex-nowrap  md:gap-4 justify-center  items-center"
		:class="{
			'md:!flex-row-reverse': props.reverse,
			'flex-col-reverse': props.reverse,
		}"
	>
		<div class="absolute -top-20 right-0 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl" />
		<div class="absolute -bottom-20 -left-20 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
		<div
			v-if="$slots.image"
			class="my-4 md:my-auto relative rounded-3xl flex justify-center sm:min-w-[400px] sm:w-[400px] xl:min-w-[500px] xl:w-[500px] 2xl:min-w-[700px] 2xl:w-[700px] "
			:class="imageAlignment"
		>
			<slot name="image" />
		</div>
		<div
			class="md:items-start flex flex-col justify-center gap-2 w-full md:w-8/12 rounded-2xl"
			:class="[position, !$slots.image ? '!w-full' : '']"
		>
			<div
				:class="{
					'md:ml-auto': props.position === 'right',
				}"
				class="flex items-center gap-4 justify-center"
			>
				<!-- Icon with gradient background -->
				<div class="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl shadow-lg">
					<slot name="icon">
						<Terminal class="w-6 h-6 text-white" />
					</slot>
				</div>

				<!-- Title -->
				<h2 class="xl:text-4xl md:text-3xl text-2xl font-bold tracking-tight text-white">
					<slot name="title" />
				</h2>
			</div>

			<div
				class="flex flex-col antialiased w-full"
				:class="position"
			>
				<p
					class="w-[100%] text-base text-center leading-relaxed text-slate-200"
					:class="textAlignment"
				>
					<slot name="description" />
				</p>
				<RainbowButtonBig
					:icon="Terminal"
					:label="t('promo.get_started')"
					:class="{
						'md:ml-auto': props.position === 'right',
						'md:mr-auto': props.position === 'left',
						textAlignment,
					}"
					@click="router.push('/account/signup')"
				/>
			</div>
		</div>
	</div>
</template>

<style scoped lang="scss">
</style>
